import { Controller } from "stimulus"

// Slide-at-a-time carousel: index tracking, arrows, JS-built dot indicators, and touch swipe.
//
// Ported from the subscriber portal's carousel_controller.js, minus the zoom overlay, lazy-load
// and drag animation. One deliberate behaviour change: the arrows stay visible on touch devices -
// the creator dashboard gets used on tablets, where hiding them leaves no way to advance.
//
// Dots are built here rather than server-side so `refresh()` can rebuild them from the live slide
// count; removing a slide after connect can't leave a stale dot behind.
export default class CarouselComponentController extends Controller {
  static targets = ["container", "indicators", "nav", "prevButton", "nextButton"]
  static values = { dotClass: String }

  connect() {
    this.index = 0
    this.slides = []
    this.boundGoTo = this.goTo.bind(this)
    this.touchMode = this.detectTouchDevice()
    if (this.touchMode) this.registerTouchEvents()
    this.refresh()
  }

  disconnect() {
    this.unregisterTouchEvents()
  }

  // Re-read the slides from the DOM, clamp the index, and rebuild everything that depends on the
  // slide count. Safe to call at any time, including before/after the caller mutates slides.
  refresh() {
    this.slides = this.hasContainerTarget ? Array.from(this.containerTarget.children) : []

    if (this.index > this.slides.length - 1) this.index = this.slides.length - 1
    if (this.index < 0) this.index = 0

    this.buildIndicators()
    this.updateSlides()
    this.updateControls()
    this.updateIndicators()
  }

  prev(event) {
    this.changeSlide(-1, event)
  }

  next(event) {
    this.changeSlide(1, event)
  }

  goTo(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    const target = event.currentTarget || event.target
    const index = parseInt(target.dataset.carouselIndex, 10)
    if (Number.isNaN(index) || index < 0 || index >= this.slides.length) return

    this.index = index
    this.scroll()
  }

  changeSlide(step, event = null) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    const newIndex = this.index + step
    if (newIndex < 0 || newIndex >= this.slides.length) return

    this.index = newIndex
    this.scroll()
  }

  scroll() {
    this.updateSlides()
    this.updateControls()
    this.updateIndicators()
  }

  updateSlides() {
    this.slides.forEach((el, i) => {
      el.style.display = i === this.index ? "block" : "none"
    })
  }

  updateControls() {
    const single = this.slides.length <= 1

    if (this.hasNavTarget) {
      this.navTarget.style.display = single ? "none" : ""
    }

    this.setArrowState(this.hasPrevButtonTarget ? this.prevButtonTarget : null, single || this.index <= 0)
    this.setArrowState(
      this.hasNextButtonTarget ? this.nextButtonTarget : null,
      single || this.index >= this.slides.length - 1
    )
  }

  setArrowState(button, disabled) {
    if (!button) return

    button.disabled = disabled
    button.style.visibility = disabled ? "hidden" : "visible"
  }

  buildIndicators() {
    if (!this.hasIndicatorsTarget) return

    const desired = this.slides.length > 1 ? this.slides.length : 0
    if (this.indicatorsTarget.children.length === desired) return

    this.indicatorsTarget.replaceChildren()

    for (let i = 0; i < desired; i++) {
      const dot = document.createElement("button")
      dot.type = "button"
      dot.className = `${this.dotClassValue} opacity-40`
      dot.dataset.carouselIndex = String(i)
      dot.setAttribute("aria-label", `Go to slide ${i + 1}`)
      // Bound directly rather than via data-action so the handler is live immediately, without
      // waiting on Stimulus's mutation observer.
      dot.addEventListener("click", this.boundGoTo)
      this.indicatorsTarget.appendChild(dot)
    }
  }

  updateIndicators() {
    if (!this.hasIndicatorsTarget) return

    Array.from(this.indicatorsTarget.children).forEach((indicator, i) => {
      const isActive = i === this.index
      indicator.classList.toggle("opacity-100", isActive)
      indicator.classList.toggle("opacity-40", !isActive)
    })
  }

  detectTouchDevice() {
    if (typeof window === "undefined") return false
    if ("ontouchstart" in window) return true
    if (navigator?.maxTouchPoints > 0 || navigator?.msMaxTouchPoints > 0) return true
    if (window.matchMedia) return window.matchMedia("(pointer: coarse)").matches
    return false
  }

  registerTouchEvents() {
    if (!this.hasContainerTarget) return

    this.boundTouchStart = this.handleTouchStart.bind(this)
    this.boundTouchMove = this.handleTouchMove.bind(this)
    this.boundTouchEnd = this.handleTouchEnd.bind(this)

    this.containerTarget.addEventListener("touchstart", this.boundTouchStart, { passive: true })
    this.containerTarget.addEventListener("touchmove", this.boundTouchMove, { passive: true })
    this.containerTarget.addEventListener("touchend", this.boundTouchEnd)
  }

  unregisterTouchEvents() {
    if (!this.hasContainerTarget || !this.boundTouchStart) return

    this.containerTarget.removeEventListener("touchstart", this.boundTouchStart, { passive: true })
    this.containerTarget.removeEventListener("touchmove", this.boundTouchMove, { passive: true })
    this.containerTarget.removeEventListener("touchend", this.boundTouchEnd)
    this.boundTouchStart = null
    this.boundTouchMove = null
    this.boundTouchEnd = null
  }

  handleTouchStart(event) {
    if (event.touches.length > 1) {
      this.touchStartX = null
      return
    }

    const touch = event.touches[0]
    this.touchStartX = touch.clientX
    this.touchStartY = touch.clientY
    this.touchDeltaX = 0
    this.touchDeltaY = 0
  }

  handleTouchMove(event) {
    if (this.touchStartX == null || event.touches.length > 1) return

    const touch = event.touches[0]
    this.touchDeltaX = touch.clientX - this.touchStartX
    this.touchDeltaY = touch.clientY - this.touchStartY
  }

  handleTouchEnd() {
    if (this.touchStartX == null) return

    const deltaX = this.touchDeltaX || 0
    const deltaY = this.touchDeltaY || 0
    this.touchStartX = null
    this.touchStartY = null

    const horizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 40
    if (!horizontalSwipe) return

    this.changeSlide(deltaX > 0 ? -1 : 1)
  }
}
